import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";

export function publicArticleClient() {
  const url = process.env["SUPABASE_URL"];
  const key = process.env["SUPABASE_ANON_KEY"] ?? process.env["SUPABASE_PUBLISHABLE_KEY"];
  if (!url || !key) throw new Error("Public article storage unavailable");
  return createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: { fetch: (input, init) => {
      const headers = new Headers(init?.headers);
      if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) headers.delete("Authorization");
      headers.set("apikey", key);
      return fetch(input, { ...init, headers });
    } },
  });
}

/**
 * Database article for a /blog slug. `owned` is true when the database is the
 * authoritative source for the slug (native Flywheel articles, or migrated
 * site articles once verified) — even when unpublished, so a draft 404s
 * instead of falling back to the code copy. `preferDb` (dev-only verification)
 * also returns unverified migrated rows.
 */
export const getFlywheelArticle = createServerFn({ method: "GET" })
  .inputValidator((raw: unknown) => z.object({ slug: z.string().max(120).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/), preferDb: z.boolean().optional() }).parse(raw))
  .handler(async ({ data }) => {
    const client = publicArticleClient();
    const [{ data: article, error }, { data: owned, error: ownedError }] = await Promise.all([
      client.from("flywheel_articles").select("*").eq("slug", data.slug).eq("status", "published").maybeSingle(),
      client.rpc("blog_slug_db_owned", { _slug: data.slug }),
    ]);
    if (error || ownedError) throw new Error("Could not read published article");
    const row = article as import("./flywheel").FlywheelArticle | null;
    const usable = row && (row.layout === "flywheel" || row.migration_verified || data.preferDb) ? row : null;
    return { article: usable, owned: Boolean(owned) || Boolean(usable) };
  });

export const getPublishedBlogList = createServerFn({ method: "GET" }).handler(async () => {
  const rows: { slug: string; title: string; meta_description: string; category: string; published_at: string | null }[] = [];
  const client = publicArticleClient();
  for (let offset = 0; ; offset += 500) {
    const { data, error } = await client.from("flywheel_articles").select("slug,title,meta_description,category,published_at").eq("status", "published").eq("layout", "flywheel").order("slug").range(offset, offset + 499);
    if (error) throw new Error("Could not read published blog list");
    rows.push(...(data ?? []));
    if (!data || data.length < 500) return rows;
  }
});
export type PublishedBlogList = Awaited<ReturnType<typeof getPublishedBlogList>>;